import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, CardActions, Button, Skeleton, Divider, Alert } from '@mui/material';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from '../utils/dateUtils';
import { QRCodeSVG } from 'qrcode.react';
import { CardGiftcard } from '@mui/icons-material';

const MyCouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user: userInfo } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not logged in
    if (!userInfo) {
      navigate('/login');
      return;
    }

    const fetchCoupons = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get('/api/coupons/my-coupons', {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        });
        console.log('Received coupons data:', data);
        
        // Проверяем, что данные пришли и имеют правильную структуру
        if (Array.isArray(data)) {
          // Фильтруем только валидные купоны
          const validCoupons = data.filter(coupon => coupon && coupon.couponId);
          console.log('Valid coupons:', validCoupons.length);
          setCoupons(validCoupons);
        } else {
          console.error('Received invalid coupons data:', data);
          setCoupons([]);
        }
        
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || 'Не удалось загрузить купоны');
        console.error('Error fetching coupons:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCoupons();
  }, [userInfo, navigate]);

  const handleViewCoupon = (couponId) => {
    navigate(`/coupons/${couponId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
        Мои купоны
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" height={40} />
                  <Skeleton variant="text" />
                  <Skeleton variant="rectangular" height={100} />
                  <Skeleton variant="text" width="40%" />
                </CardContent>
                <CardActions>
                  <Skeleton variant="rectangular" width={120} height={36} />
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : coupons.length === 0 ? (
        <Box textAlign="center" py={5}>
          <Typography variant="h6" color="textSecondary">
            У вас пока нет купонов
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {coupons.map((userCoupon) => {
            console.log('Rendering coupon:', userCoupon);
            
            // Проверяем, что у купона есть поле coupon
            if (!userCoupon.coupon) {
              console.error('Coupon data is missing for:', userCoupon);
              return null;
            }
            
            // Проверяем, что у купона есть все необходимые поля
            const couponName = userCoupon.coupon?.name || 'Приветственный купон';
            const couponDescription = userCoupon.coupon?.description || 'Скидка на бронирование';
            const couponValue = userCoupon.coupon?.value || 500;
            const couponExpiresAt = userCoupon.coupon?.expiresAt || null;
            const isExpired = couponExpiresAt ? new Date(couponExpiresAt) <= new Date() : false;
            const isCouponActive = !userCoupon.isUsed && !isExpired && (userCoupon.coupon?.isActive ?? true);
            const qrCodeValue = userCoupon.qrCode || `coupon-${userCoupon.id}`;
            
            return (
              <Grid item xs={12} md={6} lg={4} key={userCoupon.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'relative',
                    ...(userCoupon.isUsed && {
                      opacity: 0.7,
                      backgroundColor: '#f5f5f5',
                    })
                  }}
                >
                  {!userCoupon.isUsed && (
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 10, 
                        right: 10, 
                        bgcolor: 'secondary.main',
                        color: 'white',
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 2,
                        zIndex: 1
                      }}
                    >
                      <CardGiftcard fontSize="small" />
                    </Box>
                  )}
                  {userCoupon.isUsed && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) rotate(-30deg)',
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 0, 0, 0.7)',
                        color: 'white',
                        padding: '5px 20px',
                        borderRadius: '5px',
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      Использован
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {!userCoupon.isUsed && (
                        <CardGiftcard color="secondary" sx={{ mr: 1, fontSize: 28 }} />
                      )}
                      <Typography variant="h5" component="h2">
                        {couponName}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      {couponDescription}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6" color="primary" gutterBottom>
                          {couponValue} ₽
                        </Typography>
                        {userCoupon.isUsed && (
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            Использован {new Date(userCoupon.usedAt).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {userCoupon.isUsed ? (
                        <Box sx={{ 
                          bgcolor: 'error.light', 
                          color: 'error.contrastText', 
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 1,
                          fontSize: '0.8rem',
                          fontWeight: 'bold'
                        }}>
                          Использован
                        </Box>
                      ) : !isCouponActive ? (
                        <Box sx={{ 
                          bgcolor: 'error.light', 
                          color: 'error.contrastText', 
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 1,
                          fontSize: '0.8rem',
                          fontWeight: 'bold'
                        }}>
                          Просрочен
                        </Box>
                      ) : (
                        <Box sx={{ 
                          bgcolor: 'success.light', 
                          color: 'success.contrastText', 
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 1,
                          fontSize: '0.8rem',
                          fontWeight: 'bold'
                        }}>
                          Активен
                        </Box>
                      )}
                    </Box>
                    </Box>
                    {couponExpiresAt && (
                      <Typography variant="body2" color="error">
                        Действителен до: {formatDate(couponExpiresAt)}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      color="primary" 
                      variant="contained" 
                      fullWidth
                      onClick={() => handleViewCoupon(userCoupon.id)}
                      disabled={userCoupon.isUsed || !isCouponActive}
                    >
                      Показать купон
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default MyCouponsPage;
